import MultiSelectPillList from '../../../../components/MultiSelectPillList'
import SingleInputSelect from '../../../../components/SingleInputSelect'
import UserMultiselect from '../../../../components/UserMultiselect'
import { PolicyNumberField, PolicyTenantIdsField, PolicyTextField, PolicyToggleField } from './formControls'
/*
 * Configurations of the policies' inputs
 * NOTE: Most condition/grant inputs are arrays of strings. Session, filter, and toggle
 * fields use booleans, numbers, or free-text and are handled in toFormValues / buildPolicyPayload.
 *
 * For template and policy:
 * - Add new input to the constants in this file
 *
 * For templating:
 * - Filter tenant properties in filterTenantProperties
 *
 * For creating/editing a policy:
 * - Adapt values in toFormValues and buildPolicyPayload if necessary
 * - Add options to getPolicyFormOptions
 * - Add to POLICY_ACCORDION_SECTIONS and PolicyFormInputs (or to GrantControls)
 * - Validate input in POLICY_FORM_VALIDATIONS
 * - Test input in policy_management/utils.test.js
 */

export const CONDITIONS_INPUTS = {
  includeUsers: 'includeUsers',
  excludeUsers: 'excludeUsers',
  includeGroups: 'includeGroups',
  excludeGroups: 'excludeGroups',
  includeRoles: 'includeRoles',
  excludeRoles: 'excludeRoles',
  includeLocations: 'includeLocations',
  excludeLocations: 'excludeLocations',
  includeApplications: 'includeApplications',
  excludeApplications: 'excludeApplications',
  clientAppTypes: 'clientAppTypes',
  includePlatforms: 'includePlatforms',
  excludePlatforms: 'excludePlatforms',
  userRiskLevels: 'userRiskLevels',
  signInRiskLevels: 'signInRiskLevels',
  insiderRiskLevels: 'insiderRiskLevels',
  transferMethods: 'transferMethods',
  includeGuestsOrExternalUsers: 'includeGuestsOrExternalUsers',
  excludeGuestsOrExternalUsers: 'excludeGuestsOrExternalUsers',
  includeExternalTenants: 'includeExternalTenants',
  excludeExternalTenants: 'excludeExternalTenants',
  includeUserActions: 'includeUserActions',
  includeAuthenticationContextClassReferences: 'includeAuthenticationContextClassReferences',
  applicationFilterMode: 'applicationFilterMode',
  applicationFilterRule: 'applicationFilterRule',
  deviceFilterMode: 'deviceFilterMode',
  deviceFilterRule: 'deviceFilterRule',
  includeDevices: 'includeDevices',
  excludeDevices: 'excludeDevices',
  includeServicePrincipals: 'includeServicePrincipals',
  excludeServicePrincipals: 'excludeServicePrincipals',
  servicePrincipalFilterMode: 'servicePrincipalFilterMode',
  servicePrincipalFilterRule: 'servicePrincipalFilterRule',
  servicePrincipalRiskLevels: 'servicePrincipalRiskLevels',
  agentIdRiskLevels: 'agentIdRiskLevels',
}

export const GRANT_CONTROLS_INPUTS = {
  builtInControls: 'builtInControls',
  grantControlsOperator: 'grantControlsOperator',
  customAuthenticationFactors: 'customAuthenticationFactors',
  authenticationStrength: 'authenticationStrength',
  termsOfUse: 'termsOfUse',
}

export const SESSION_CONTROLS_INPUTS = {
  signInFrequencyIsEnabled: 'signInFrequencyIsEnabled',
  signInFrequencyValue: 'signInFrequencyValue',
  signInFrequencyType: 'signInFrequencyType',
  signInFrequencyInterval: 'signInFrequencyInterval',
  signInFrequencyAuthenticationType: 'signInFrequencyAuthenticationType',
  persistentBrowserIsEnabled: 'persistentBrowserIsEnabled',
  persistentBrowserMode: 'persistentBrowserMode',
  applicationEnforcedRestrictionsIsEnabled: 'applicationEnforcedRestrictionsIsEnabled',
  cloudAppSecurityIsEnabled: 'cloudAppSecurityIsEnabled',
  cloudAppSecurityType: 'cloudAppSecurityType',
  disableResilienceDefaults: 'disableResilienceDefaults',
  continuousAccessEvaluationMode: 'continuousAccessEvaluationMode',
  secureSignInSessionIsEnabled: 'secureSignInSessionIsEnabled',
  globalSecureAccessFilteringProfileIsEnabled: 'globalSecureAccessFilteringProfileIsEnabled',
  globalSecureAccessFilteringProfileId: 'globalSecureAccessFilteringProfileId',
}

export const GENERAL_INPUTS = {
  policyStatus: 'policyStatus',
}

export const CONTINUOUS_ACCESS_EVALUATION_MODES = {
  DISABLED: 'disabled',
  STRICT_ENFORCEMENT: 'strictEnforcement',
  STRICT_LOCATION: 'strictLocation',
}

/* Input keys (name of the inputs in the policy object) */
export const INPUT_KEYS = {
  ...CONDITIONS_INPUTS,
  ...GRANT_CONTROLS_INPUTS,
  ...SESSION_CONTROLS_INPUTS,
  ...GENERAL_INPUTS,
}

export const INPUT_LIST = Object.values(INPUT_KEYS)
export const CONDITIONS_INPUTS_LIST = Object.values(CONDITIONS_INPUTS)
export const GRANT_CONTROLS_INPUTS_LIST = Object.values(GRANT_CONTROLS_INPUTS)
export const SESSION_CONTROLS_INPUTS_LIST = Object.values(SESSION_CONTROLS_INPUTS)
export const GENERAL_INPUTS_LIST = Object.values(GENERAL_INPUTS)

const KNOWN_INPUTS = new Set(INPUT_LIST)

// A template saved while the app still offered an input it has since retired (Exclude
// Authentication Context, which Graph never supported) can still list it as a prompt. No field
// renders it, so a required one would block deploy on a value nobody can enter — it's dropped.
export const withoutRetiredPromptedInputs = (promptedInputs) =>
  (promptedInputs ?? []).filter((input) => KNOWN_INPUTS.has(input?.name))

export const isGroupInput = (inputName) => [INPUT_KEYS.includeGroups, INPUT_KEYS.excludeGroups].includes(inputName)

export const isNamedLocationInput = (inputName) =>
  [INPUT_KEYS.includeLocations, INPUT_KEYS.excludeLocations].includes(inputName)

export const isApplicationInput = (inputName) =>
  [INPUT_KEYS.includeApplications, INPUT_KEYS.excludeApplications].includes(inputName)

export const isUserInput = (inputName) => [INPUT_KEYS.includeUsers, INPUT_KEYS.excludeUsers].includes(inputName)
export const isRoleInput = (inputName) => [INPUT_KEYS.includeRoles, INPUT_KEYS.excludeRoles].includes(inputName)
export const isServicePrincipalInput = (inputName) =>
  [INPUT_KEYS.includeServicePrincipals, INPUT_KEYS.excludeServicePrincipals].includes(inputName)
export const isCustomAuthenticationFactorInput = (inputName) => inputName === INPUT_KEYS.customAuthenticationFactors

export const isAuthenticationContextInput = (inputName) =>
  inputName === INPUT_KEYS.includeAuthenticationContextClassReferences

export const isRemovablePolicyInput = (inputName) =>
  Boolean(CONDITIONS_INPUTS[inputName] || SESSION_CONTROLS_INPUTS[inputName])

/* Options for the policy template and Add input(s) */
export const INPUT_OPTIONS = [
  { label: 'Include Users', value: INPUT_KEYS.includeUsers },
  { label: 'Exclude Users', value: INPUT_KEYS.excludeUsers },
  { label: 'Include Groups', value: INPUT_KEYS.includeGroups },
  { label: 'Exclude Groups', value: INPUT_KEYS.excludeGroups },
  { label: 'Include Roles', value: INPUT_KEYS.includeRoles },
  { label: 'Exclude Roles', value: INPUT_KEYS.excludeRoles },
  { label: 'Include Guest or External Users', value: INPUT_KEYS.includeGuestsOrExternalUsers },
  { label: 'Exclude Guest or External Users', value: INPUT_KEYS.excludeGuestsOrExternalUsers },
  { label: 'Include External Tenants', value: INPUT_KEYS.includeExternalTenants },
  { label: 'Exclude External Tenants', value: INPUT_KEYS.excludeExternalTenants },
  { label: 'Include Locations', value: INPUT_KEYS.includeLocations },
  { label: 'Exclude Locations', value: INPUT_KEYS.excludeLocations },
  { label: 'Include Applications', value: INPUT_KEYS.includeApplications },
  { label: 'Exclude Applications', value: INPUT_KEYS.excludeApplications },
  { label: 'User Actions', value: INPUT_KEYS.includeUserActions },
  { label: 'Include Authentication Context', value: INPUT_KEYS.includeAuthenticationContextClassReferences },
  { label: 'Application filter mode', value: INPUT_KEYS.applicationFilterMode },
  { label: 'Application filter rule', value: INPUT_KEYS.applicationFilterRule },
  { label: 'Client Apps', value: INPUT_KEYS.clientAppTypes },
  { label: 'Include Device Platforms', value: INPUT_KEYS.includePlatforms },
  { label: 'Exclude Device Platforms', value: INPUT_KEYS.excludePlatforms },
  { label: 'Device filter mode', value: INPUT_KEYS.deviceFilterMode },
  { label: 'Device filter rule', value: INPUT_KEYS.deviceFilterRule },
  { label: 'Include devices', value: INPUT_KEYS.includeDevices },
  { label: 'Exclude devices', value: INPUT_KEYS.excludeDevices },
  { label: 'Include service principals', value: INPUT_KEYS.includeServicePrincipals },
  { label: 'Exclude service principals', value: INPUT_KEYS.excludeServicePrincipals },
  { label: 'Service principal filter mode', value: INPUT_KEYS.servicePrincipalFilterMode },
  { label: 'Service principal filter rule', value: INPUT_KEYS.servicePrincipalFilterRule },
  { label: 'User Risk', value: INPUT_KEYS.userRiskLevels },
  { label: 'Sign-in Risk', value: INPUT_KEYS.signInRiskLevels },
  { label: 'Insider Risk', value: INPUT_KEYS.insiderRiskLevels },
  { label: 'Service principal risk', value: INPUT_KEYS.servicePrincipalRiskLevels },
  { label: 'Agent ID risk', value: INPUT_KEYS.agentIdRiskLevels },
  { label: 'Transfer methods', value: INPUT_KEYS.transferMethods },
  { label: 'Sign-in frequency enabled', value: INPUT_KEYS.signInFrequencyIsEnabled },
  { label: 'Sign-in frequency value', value: INPUT_KEYS.signInFrequencyValue },
  { label: 'Sign-in frequency unit', value: INPUT_KEYS.signInFrequencyType },
  { label: 'Sign-in frequency interval', value: INPUT_KEYS.signInFrequencyInterval },
  { label: 'Sign-in frequency authentication type', value: INPUT_KEYS.signInFrequencyAuthenticationType },
  { label: 'Persistent browser enabled', value: INPUT_KEYS.persistentBrowserIsEnabled },
  { label: 'Persistent browser mode', value: INPUT_KEYS.persistentBrowserMode },
  { label: 'App enforced restrictions', value: INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled },
  { label: 'Cloud app security enabled', value: INPUT_KEYS.cloudAppSecurityIsEnabled },
  { label: 'Cloud app security type', value: INPUT_KEYS.cloudAppSecurityType },
  { label: 'Disable resilience defaults', value: INPUT_KEYS.disableResilienceDefaults },
  { label: 'Continuous access evaluation', value: INPUT_KEYS.continuousAccessEvaluationMode },
  { label: 'Secure sign-in session', value: INPUT_KEYS.secureSignInSessionIsEnabled },
  { label: 'Global Secure Access profile enabled', value: INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled },
  { label: 'Global Secure Access profile ID', value: INPUT_KEYS.globalSecureAccessFilteringProfileId },
]

// Grant controls rather than condition or session inputs, so they aren't in INPUT_OPTIONS. They
// are only offered as template prompted inputs with policy requirements on, where a custom
// authentication strength or custom authentication factor is carried by name and resolved at
// deploy.
const AUTHENTICATION_STRENGTH_INPUT_OPTION = {
  label: 'Require authentication strength',
  value: INPUT_KEYS.authenticationStrength,
}
const CUSTOM_AUTHENTICATION_FACTORS_INPUT_OPTION = {
  label: 'Custom authentication factors',
  value: INPUT_KEYS.customAuthenticationFactors,
}
const REQUIREMENT_ONLY_INPUTS = new Set([INPUT_KEYS.authenticationStrength, INPUT_KEYS.customAuthenticationFactors])
const PROMPTED_INPUT_OPTIONS_WITH_REQUIREMENTS = [
  ...INPUT_OPTIONS,
  AUTHENTICATION_STRENGTH_INPUT_OPTION,
  CUSTOM_AUTHENTICATION_FACTORS_INPUT_OPTION,
]

export const getPromptedInputOptions = (isPolicyRequirementsEnabled) =>
  isPolicyRequirementsEnabled ? PROMPTED_INPUT_OPTIONS_WITH_REQUIREMENTS : INPUT_OPTIONS

export const INPUT_OPTIONS_MAP = {}
PROMPTED_INPUT_OPTIONS_WITH_REQUIREMENTS.forEach((option) => {
  INPUT_OPTIONS_MAP[option.value] = option.label
})

// Whether a Policy Details field can be collected at deploy (prompted or resolved automatically).
// INPUT_OPTIONS_MAP names every such field, but the grant control ones only apply with policy
// requirements on.
export const isPromptableInput = (input, isPolicyRequirementsEnabled) =>
  Boolean(INPUT_OPTIONS_MAP[input]) && (!REQUIREMENT_ONLY_INPUTS.has(input) || isPolicyRequirementsEnabled)

export const INPUT_ADD_DEPENDENCIES = {
  // External tenants only narrow a guest/external user condition, so they need its user types.
  [INPUT_KEYS.includeExternalTenants]: [INPUT_KEYS.includeGuestsOrExternalUsers],
  [INPUT_KEYS.excludeExternalTenants]: [INPUT_KEYS.excludeGuestsOrExternalUsers],
  [INPUT_KEYS.applicationFilterMode]: [INPUT_KEYS.applicationFilterRule],
  [INPUT_KEYS.applicationFilterRule]: [INPUT_KEYS.applicationFilterMode],
  [INPUT_KEYS.deviceFilterMode]: [INPUT_KEYS.deviceFilterRule],
  [INPUT_KEYS.deviceFilterRule]: [INPUT_KEYS.deviceFilterMode],
  [INPUT_KEYS.servicePrincipalFilterMode]: [INPUT_KEYS.servicePrincipalFilterRule],
  [INPUT_KEYS.servicePrincipalFilterRule]: [INPUT_KEYS.servicePrincipalFilterMode],
  [INPUT_KEYS.signInFrequencyIsEnabled]: [INPUT_KEYS.signInFrequencyValue, INPUT_KEYS.signInFrequencyType],
  [INPUT_KEYS.signInFrequencyValue]: [INPUT_KEYS.signInFrequencyIsEnabled, INPUT_KEYS.signInFrequencyType],
  [INPUT_KEYS.signInFrequencyType]: [INPUT_KEYS.signInFrequencyIsEnabled, INPUT_KEYS.signInFrequencyValue],
  [INPUT_KEYS.persistentBrowserIsEnabled]: [INPUT_KEYS.persistentBrowserMode],
  [INPUT_KEYS.persistentBrowserMode]: [INPUT_KEYS.persistentBrowserIsEnabled],
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: [INPUT_KEYS.cloudAppSecurityType],
  [INPUT_KEYS.cloudAppSecurityType]: [INPUT_KEYS.cloudAppSecurityIsEnabled],
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]: [INPUT_KEYS.globalSecureAccessFilteringProfileId],
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled],
}

export const INPUT_ADD_DEFAULTS = {
  [INPUT_KEYS.signInFrequencyIsEnabled]: true,
  [INPUT_KEYS.persistentBrowserIsEnabled]: true,
  [INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled]: true,
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: true,
  [INPUT_KEYS.secureSignInSessionIsEnabled]: true,
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]: true,
  [INPUT_KEYS.continuousAccessEvaluationMode]: CONTINUOUS_ACCESS_EVALUATION_MODES.STRICT_ENFORCEMENT,
  [INPUT_KEYS.applicationFilterMode]: 'include',
  [INPUT_KEYS.deviceFilterMode]: 'include',
  [INPUT_KEYS.servicePrincipalFilterMode]: 'include',
}

/* path of the inputs in the policy json */
export const INPUT_POLICY_PATH = {
  // conditions inputs
  [INPUT_KEYS.includeUsers]: 'conditions.users.includeUsers',
  [INPUT_KEYS.excludeUsers]: 'conditions.users.excludeUsers',
  [INPUT_KEYS.includeGroups]: 'conditions.users.includeGroups',
  [INPUT_KEYS.excludeGroups]: 'conditions.users.excludeGroups',
  [INPUT_KEYS.includeRoles]: 'conditions.users.includeRoles',
  [INPUT_KEYS.excludeRoles]: 'conditions.users.excludeRoles',
  [INPUT_KEYS.includeLocations]: 'conditions.locations.includeLocations',
  [INPUT_KEYS.excludeLocations]: 'conditions.locations.excludeLocations',
  [INPUT_KEYS.includeApplications]: 'conditions.applications.includeApplications',
  [INPUT_KEYS.excludeApplications]: 'conditions.applications.excludeApplications',
  [INPUT_KEYS.clientAppTypes]: 'conditions.clientAppTypes',
  [INPUT_KEYS.includePlatforms]: 'conditions.platforms.includePlatforms',
  [INPUT_KEYS.excludePlatforms]: 'conditions.platforms.excludePlatforms',
  [INPUT_KEYS.userRiskLevels]: 'conditions.userRiskLevels',
  [INPUT_KEYS.signInRiskLevels]: 'conditions.signInRiskLevels',
  [INPUT_KEYS.insiderRiskLevels]: 'conditions.insiderRiskLevels',
  [INPUT_KEYS.transferMethods]: 'conditions.authenticationFlows.transferMethods',
  [INPUT_KEYS.includeGuestsOrExternalUsers]: 'conditions.users.includeGuestsOrExternalUsers.guestOrExternalUserTypes',
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: 'conditions.users.excludeGuestsOrExternalUsers.guestOrExternalUserTypes',
  [INPUT_KEYS.includeExternalTenants]: 'conditions.users.includeGuestsOrExternalUsers.externalTenants.members',
  [INPUT_KEYS.excludeExternalTenants]: 'conditions.users.excludeGuestsOrExternalUsers.externalTenants.members',
  [INPUT_KEYS.includeUserActions]: 'conditions.applications.includeUserActions',
  [INPUT_KEYS.includeAuthenticationContextClassReferences]:
    'conditions.applications.includeAuthenticationContextClassReferences',
  [INPUT_KEYS.applicationFilterMode]: 'conditions.applications.applicationFilter.mode',
  [INPUT_KEYS.applicationFilterRule]: 'conditions.applications.applicationFilter.rule',
  [INPUT_KEYS.deviceFilterMode]: 'conditions.devices.deviceFilter.mode',
  [INPUT_KEYS.deviceFilterRule]: 'conditions.devices.deviceFilter.rule',
  [INPUT_KEYS.includeDevices]: 'conditions.devices.includeDevices',
  [INPUT_KEYS.excludeDevices]: 'conditions.devices.excludeDevices',
  [INPUT_KEYS.includeServicePrincipals]: 'conditions.clientApplications.includeServicePrincipals',
  [INPUT_KEYS.excludeServicePrincipals]: 'conditions.clientApplications.excludeServicePrincipals',
  [INPUT_KEYS.servicePrincipalFilterMode]: 'conditions.clientApplications.servicePrincipalFilter.mode',
  [INPUT_KEYS.servicePrincipalFilterRule]: 'conditions.clientApplications.servicePrincipalFilter.rule',
  [INPUT_KEYS.servicePrincipalRiskLevels]: 'conditions.servicePrincipalRiskLevels',
  [INPUT_KEYS.agentIdRiskLevels]: 'conditions.agentIdRiskLevels',

  // grant controls
  [INPUT_KEYS.builtInControls]: 'grantControls.builtInControls',
  [INPUT_KEYS.grantControlsOperator]: 'grantControls.operator',
  [INPUT_KEYS.customAuthenticationFactors]: 'grantControls.customAuthenticationFactors',
  [INPUT_KEYS.authenticationStrength]: 'grantControls.authenticationStrength.id',
  [INPUT_KEYS.termsOfUse]: 'grantControls.termsOfUse',

  // session controls
  [INPUT_KEYS.signInFrequencyIsEnabled]: 'sessionControls.signInFrequency.isEnabled',
  [INPUT_KEYS.signInFrequencyValue]: 'sessionControls.signInFrequency.value',
  [INPUT_KEYS.signInFrequencyType]: 'sessionControls.signInFrequency.type',
  [INPUT_KEYS.signInFrequencyInterval]: 'sessionControls.signInFrequency.frequencyInterval',
  [INPUT_KEYS.signInFrequencyAuthenticationType]: 'sessionControls.signInFrequency.authenticationType',
  [INPUT_KEYS.persistentBrowserIsEnabled]: 'sessionControls.persistentBrowser.isEnabled',
  [INPUT_KEYS.persistentBrowserMode]: 'sessionControls.persistentBrowser.mode',
  [INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled]: 'sessionControls.applicationEnforcedRestrictions.isEnabled',
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: 'sessionControls.cloudAppSecurity.isEnabled',
  [INPUT_KEYS.cloudAppSecurityType]: 'sessionControls.cloudAppSecurity.cloudAppSecurityType',
  [INPUT_KEYS.disableResilienceDefaults]: 'sessionControls.disableResilienceDefaults',
  [INPUT_KEYS.continuousAccessEvaluationMode]: 'sessionControls.continuousAccessEvaluation.mode',
  [INPUT_KEYS.secureSignInSessionIsEnabled]: 'sessionControls.secureSignInSession.isEnabled',
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]:
    'sessionControls.globalSecureAccessFilteringProfile.isEnabled',
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: 'sessionControls.globalSecureAccessFilteringProfile.profileId',

  // general
  [INPUT_KEYS.policyStatus]: 'state',
}

export const INPUT_TITLE_MAP = {
  [INPUT_KEYS.includeUsers]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_USERS',
  [INPUT_KEYS.excludeUsers]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_USERS',
  [INPUT_KEYS.includeGroups]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_GROUPS',
  [INPUT_KEYS.excludeGroups]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_GROUPS',
  [INPUT_KEYS.includeRoles]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_ROLES',
  [INPUT_KEYS.excludeRoles]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_ROLES',
  [INPUT_KEYS.includeLocations]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_LOCATIONS',
  [INPUT_KEYS.excludeLocations]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_LOCATIONS',
  [INPUT_KEYS.includeApplications]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_APPLICATIONS',
  [INPUT_KEYS.excludeApplications]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_APPLICATIONS',
  [INPUT_KEYS.clientAppTypes]: 'POLICY_TEMPLATES.INPUTS.CLIENT_APPS',
  [INPUT_KEYS.includePlatforms]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_PLATFORMS',
  [INPUT_KEYS.excludePlatforms]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_PLATFORMS',
  [INPUT_KEYS.userRiskLevels]: 'POLICY_TEMPLATES.INPUTS.USER_RISK',
  [INPUT_KEYS.signInRiskLevels]: 'POLICY_TEMPLATES.INPUTS.SIGNIN_RISK',
  [INPUT_KEYS.insiderRiskLevels]: 'POLICY_TEMPLATES.INPUTS.INSIDER_RISK',
  [INPUT_KEYS.transferMethods]: 'POLICY_TEMPLATES.INPUTS.TRANSFER_METHODS',
  [INPUT_KEYS.includeGuestsOrExternalUsers]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_GUEST_USERS',
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_GUEST_USERS',
  [INPUT_KEYS.includeExternalTenants]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_EXTERNAL_TENANTS',
  [INPUT_KEYS.excludeExternalTenants]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_EXTERNAL_TENANTS',
  [INPUT_KEYS.includeUserActions]: 'POLICY_TEMPLATES.INPUTS.USER_ACTIONS',
  [INPUT_KEYS.includeAuthenticationContextClassReferences]: 'POLICY_TEMPLATES.INPUTS.AUTH_CONTEXT',
  [INPUT_KEYS.applicationFilterMode]: 'POLICY_TEMPLATES.INPUTS.APPLICATION_FILTER_MODE',
  [INPUT_KEYS.applicationFilterRule]: 'POLICY_TEMPLATES.INPUTS.APPLICATION_FILTER_RULE',
  [INPUT_KEYS.deviceFilterMode]: 'POLICY_TEMPLATES.INPUTS.DEVICE_FILTER_MODE',
  [INPUT_KEYS.deviceFilterRule]: 'POLICY_TEMPLATES.INPUTS.DEVICE_FILTER_RULE',
  [INPUT_KEYS.includeDevices]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_DEVICES',
  [INPUT_KEYS.excludeDevices]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_DEVICES',
  [INPUT_KEYS.includeServicePrincipals]: 'POLICY_TEMPLATES.INPUTS.INCLUDE_SERVICE_PRINCIPALS',
  [INPUT_KEYS.excludeServicePrincipals]: 'POLICY_TEMPLATES.INPUTS.EXCLUDE_SERVICE_PRINCIPALS',
  [INPUT_KEYS.servicePrincipalFilterMode]: 'POLICY_TEMPLATES.INPUTS.SERVICE_PRINCIPAL_FILTER_MODE',
  [INPUT_KEYS.servicePrincipalFilterRule]: 'POLICY_TEMPLATES.INPUTS.SERVICE_PRINCIPAL_FILTER_RULE',
  [INPUT_KEYS.servicePrincipalRiskLevels]: 'POLICY_TEMPLATES.INPUTS.SERVICE_PRINCIPAL_RISK',
  [INPUT_KEYS.agentIdRiskLevels]: 'POLICY_TEMPLATES.INPUTS.AGENT_ID_RISK',

  // grant controls
  [INPUT_KEYS.builtInControls]: 'POLICY_TEMPLATES.BUILT_IN_CONTROLS',
  [INPUT_KEYS.authenticationStrength]: 'POLICY_TEMPLATES.REQUIRE_AUTHENTICATION_STRENGTH',
  [INPUT_KEYS.customAuthenticationFactors]: 'POLICY_TEMPLATES.CUSTOM_AUTH_FACTORS',
  [INPUT_KEYS.grantControlsOperator]: 'POLICY_TEMPLATES.FOR_MULTIPLE_CONTROLS',
  [INPUT_KEYS.termsOfUse]: 'POLICY_TEMPLATES.TERMS_OF_USE',

  // session controls
  [INPUT_KEYS.signInFrequencyIsEnabled]: 'POLICY_TEMPLATES.INPUTS.SIGN_IN_FREQUENCY_ENABLED',
  [INPUT_KEYS.signInFrequencyValue]: 'POLICY_TEMPLATES.INPUTS.SIGN_IN_FREQUENCY_VALUE',
  [INPUT_KEYS.signInFrequencyType]: 'POLICY_TEMPLATES.INPUTS.SIGN_IN_FREQUENCY_UNIT',
  [INPUT_KEYS.signInFrequencyInterval]: 'POLICY_TEMPLATES.INPUTS.SIGN_IN_FREQUENCY_INTERVAL',
  [INPUT_KEYS.signInFrequencyAuthenticationType]: 'POLICY_TEMPLATES.INPUTS.SIGN_IN_FREQUENCY_AUTH_TYPE',
  [INPUT_KEYS.persistentBrowserIsEnabled]: 'POLICY_TEMPLATES.INPUTS.PERSISTENT_BROWSER_ENABLED',
  [INPUT_KEYS.persistentBrowserMode]: 'POLICY_TEMPLATES.INPUTS.PERSISTENT_BROWSER_MODE',
  [INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled]: 'POLICY_TEMPLATES.INPUTS.APP_ENFORCED_RESTRICTIONS',
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: 'POLICY_TEMPLATES.INPUTS.CLOUD_APP_SECURITY_ENABLED',
  [INPUT_KEYS.cloudAppSecurityType]: 'POLICY_TEMPLATES.INPUTS.CLOUD_APP_SECURITY_TYPE',
  [INPUT_KEYS.disableResilienceDefaults]: 'POLICY_TEMPLATES.INPUTS.DISABLE_RESILIENCE_DEFAULTS',
  [INPUT_KEYS.continuousAccessEvaluationMode]: 'POLICY_TEMPLATES.INPUTS.CONTINUOUS_ACCESS_EVALUATION',
  [INPUT_KEYS.secureSignInSessionIsEnabled]: 'POLICY_TEMPLATES.INPUTS.SECURE_SIGN_IN_SESSION',
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]: 'POLICY_TEMPLATES.INPUTS.GSA_FILTERING_PROFILE_ENABLED',
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: 'POLICY_TEMPLATES.INPUTS.GSA_FILTERING_PROFILE_ID',

  // general
  [INPUT_KEYS.policyStatus]: 'GENERAL.STATUS',
}

export const INPUT_FORM_COMPONENT = {
  [INPUT_KEYS.includeUsers]: UserMultiselect,
  [INPUT_KEYS.excludeUsers]: UserMultiselect,
  [INPUT_KEYS.includeGroups]: MultiSelectPillList,
  [INPUT_KEYS.excludeGroups]: MultiSelectPillList,
  [INPUT_KEYS.includeRoles]: MultiSelectPillList,
  [INPUT_KEYS.excludeRoles]: MultiSelectPillList,
  [INPUT_KEYS.includeLocations]: MultiSelectPillList,
  [INPUT_KEYS.excludeLocations]: MultiSelectPillList,
  [INPUT_KEYS.includeApplications]: MultiSelectPillList,
  [INPUT_KEYS.excludeApplications]: MultiSelectPillList,
  [INPUT_KEYS.clientAppTypes]: MultiSelectPillList,
  [INPUT_KEYS.includePlatforms]: MultiSelectPillList,
  [INPUT_KEYS.excludePlatforms]: MultiSelectPillList,
  [INPUT_KEYS.userRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.signInRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.insiderRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.transferMethods]: MultiSelectPillList,
  [INPUT_KEYS.includeGuestsOrExternalUsers]: MultiSelectPillList,
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: MultiSelectPillList,
  [INPUT_KEYS.includeExternalTenants]: PolicyTenantIdsField,
  [INPUT_KEYS.excludeExternalTenants]: PolicyTenantIdsField,
  [INPUT_KEYS.includeUserActions]: SingleInputSelect,
  [INPUT_KEYS.includeAuthenticationContextClassReferences]: MultiSelectPillList,
  [INPUT_KEYS.applicationFilterMode]: SingleInputSelect,
  [INPUT_KEYS.applicationFilterRule]: PolicyTextField,
  [INPUT_KEYS.deviceFilterMode]: SingleInputSelect,
  [INPUT_KEYS.deviceFilterRule]: PolicyTextField,
  [INPUT_KEYS.includeDevices]: MultiSelectPillList,
  [INPUT_KEYS.excludeDevices]: MultiSelectPillList,
  [INPUT_KEYS.includeServicePrincipals]: MultiSelectPillList,
  [INPUT_KEYS.excludeServicePrincipals]: MultiSelectPillList,
  [INPUT_KEYS.servicePrincipalFilterMode]: SingleInputSelect,
  [INPUT_KEYS.servicePrincipalFilterRule]: PolicyTextField,
  [INPUT_KEYS.servicePrincipalRiskLevels]: MultiSelectPillList,
  [INPUT_KEYS.agentIdRiskLevels]: MultiSelectPillList,

  // grant controls
  [INPUT_KEYS.builtInControls]: MultiSelectPillList,
  [INPUT_KEYS.authenticationStrength]: SingleInputSelect,
  [INPUT_KEYS.customAuthenticationFactors]: MultiSelectPillList,
  [INPUT_KEYS.grantControlsOperator]: SingleInputSelect,
  [INPUT_KEYS.termsOfUse]: MultiSelectPillList,

  // session controls
  [INPUT_KEYS.signInFrequencyIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.signInFrequencyValue]: PolicyNumberField,
  [INPUT_KEYS.signInFrequencyType]: SingleInputSelect,
  [INPUT_KEYS.signInFrequencyInterval]: SingleInputSelect,
  [INPUT_KEYS.signInFrequencyAuthenticationType]: SingleInputSelect,
  [INPUT_KEYS.persistentBrowserIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.persistentBrowserMode]: SingleInputSelect,
  [INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.cloudAppSecurityType]: SingleInputSelect,
  [INPUT_KEYS.disableResilienceDefaults]: PolicyToggleField,
  [INPUT_KEYS.continuousAccessEvaluationMode]: SingleInputSelect,
  [INPUT_KEYS.secureSignInSessionIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]: PolicyToggleField,
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: PolicyTextField,

  // general
  [INPUT_KEYS.policyStatus]: SingleInputSelect,
}

export const INPUT_FORM_PROPS = {
  [INPUT_KEYS.includeAuthenticationContextClassReferences]: { creatable: true },
  [INPUT_KEYS.includeDevices]: { creatable: true },
  [INPUT_KEYS.excludeDevices]: { creatable: true },
  [INPUT_KEYS.includeServicePrincipals]: { creatable: true },
  [INPUT_KEYS.excludeServicePrincipals]: { creatable: true },
  [INPUT_KEYS.termsOfUse]: { creatable: true },
  [INPUT_KEYS.applicationFilterRule]: { placeholder: 'device.operatingSystem -eq "Windows"' },
  [INPUT_KEYS.deviceFilterRule]: { placeholder: 'device.trustType -eq "AzureAD"' },
  [INPUT_KEYS.servicePrincipalFilterRule]: { placeholder: 'appId -eq "00000000-0000-0000-0000-000000000000"' },
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: {
    placeholder: '00000000-0000-0000-0000-000000000000',
  },
}

export const INPUT_TO_EMPTY_VALUE = {
  [INPUT_KEYS.includeUsers]: [],
  [INPUT_KEYS.excludeUsers]: [],
  [INPUT_KEYS.includeGroups]: [],
  [INPUT_KEYS.excludeGroups]: [],
  [INPUT_KEYS.includeRoles]: [],
  [INPUT_KEYS.excludeRoles]: [],
  [INPUT_KEYS.includeLocations]: [],
  [INPUT_KEYS.excludeLocations]: [],
  [INPUT_KEYS.includeApplications]: [],
  [INPUT_KEYS.excludeApplications]: [],
  [INPUT_KEYS.clientAppTypes]: [],
  [INPUT_KEYS.includePlatforms]: [],
  [INPUT_KEYS.excludePlatforms]: [],
  [INPUT_KEYS.userRiskLevels]: [],
  [INPUT_KEYS.signInRiskLevels]: [],
  [INPUT_KEYS.insiderRiskLevels]: [],
  [INPUT_KEYS.transferMethods]: [],
  [INPUT_KEYS.includeGuestsOrExternalUsers]: [],
  [INPUT_KEYS.excludeGuestsOrExternalUsers]: [],
  [INPUT_KEYS.includeExternalTenants]: [],
  [INPUT_KEYS.excludeExternalTenants]: [],
  [INPUT_KEYS.includeUserActions]: null,
  [INPUT_KEYS.includeAuthenticationContextClassReferences]: [],
  [INPUT_KEYS.applicationFilterMode]: null,
  [INPUT_KEYS.applicationFilterRule]: null,
  [INPUT_KEYS.deviceFilterMode]: null,
  [INPUT_KEYS.deviceFilterRule]: null,
  [INPUT_KEYS.includeDevices]: [],
  [INPUT_KEYS.excludeDevices]: [],
  [INPUT_KEYS.includeServicePrincipals]: [],
  [INPUT_KEYS.excludeServicePrincipals]: [],
  [INPUT_KEYS.servicePrincipalFilterMode]: null,
  [INPUT_KEYS.servicePrincipalFilterRule]: null,
  [INPUT_KEYS.servicePrincipalRiskLevels]: [],
  [INPUT_KEYS.agentIdRiskLevels]: [],

  // grant controls
  [INPUT_KEYS.builtInControls]: [],
  [INPUT_KEYS.authenticationStrength]: null,
  [INPUT_KEYS.customAuthenticationFactors]: [],
  [INPUT_KEYS.grantControlsOperator]: null,
  [INPUT_KEYS.termsOfUse]: [],

  // session controls
  [INPUT_KEYS.signInFrequencyIsEnabled]: null,
  [INPUT_KEYS.signInFrequencyValue]: null,
  [INPUT_KEYS.signInFrequencyType]: null,
  [INPUT_KEYS.signInFrequencyInterval]: null,
  [INPUT_KEYS.signInFrequencyAuthenticationType]: null,
  [INPUT_KEYS.persistentBrowserIsEnabled]: null,
  [INPUT_KEYS.persistentBrowserMode]: null,
  [INPUT_KEYS.applicationEnforcedRestrictionsIsEnabled]: null,
  [INPUT_KEYS.cloudAppSecurityIsEnabled]: null,
  [INPUT_KEYS.cloudAppSecurityType]: null,
  [INPUT_KEYS.disableResilienceDefaults]: null,
  [INPUT_KEYS.continuousAccessEvaluationMode]: null,
  [INPUT_KEYS.secureSignInSessionIsEnabled]: null,
  [INPUT_KEYS.globalSecureAccessFilteringProfileIsEnabled]: null,
  [INPUT_KEYS.globalSecureAccessFilteringProfileId]: null,

  // general
  [INPUT_KEYS.policyStatus]: null,
}
